<template>
  <v-card flat>
    <v-card-text>
      <v-container>
        <!-- Скелетон для информации об устройстве -->
        <v-row v-if="loadingDetails && !deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('autofon.device_info') }}
            </h3>
            <v-row>
              <v-col v-for="i in 8" :key="'skeleton-info-' + i" cols="6" sm="4" md="3" lg="2">
                <v-skeleton-loader type="list-item-two-line" class="mx-0"></v-skeleton-loader>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <!-- Информация об устройстве -->
        <v-row v-if="deviceData">
          <v-col cols="12">
            <h3 class="mb-3">
              <v-icon left>mdi-information-outline</v-icon>
              {{ $t('autofon.device_info') }}
              <v-progress-circular v-if="loadingDetails" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row>
              <v-col 
                v-for="item in deviceInfoItems" 
                :key="item.label" 
                cols="6" 
                sm="4" 
                md="3" 
                lg="2"
              >
                <v-list-item dense class="px-0">
                  <v-list-item-icon class="mr-2"><v-icon small :color="item.color || ''">{{ item.icon }}</v-icon></v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-subtitle class="text-caption">{{ item.label }}</v-list-item-subtitle>
                    <v-list-item-title :class="item.textColor ? item.textColor + '--text' : ''">{{ item.value }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-col>
            </v-row>
            
            <!-- Статус устройства -->
            <v-row class="mt-2">
              <v-col cols="12">
                <v-chip :color="statusColor" dark small class="mr-2">
                  <v-icon left small>mdi-wifi</v-icon>
                  {{ $t('autofon.status_' + (deviceData.status || 'unknown')) }}
                </v-chip>
                <v-chip v-if="deviceData.ignition === 'on'" color="success" dark small class="mr-2">
                  <v-icon left small>mdi-key</v-icon>
                  {{ $t('autofon.ignition_on') }}
                </v-chip>
                <v-chip v-if="deviceData.moving" color="blue" dark small class="mr-2">
                  <v-icon left small>mdi-car</v-icon>
                  {{ $t('autofon.moving') }}
                </v-chip>
                <v-chip v-if="deviceData.alarm === 'on'" color="error" dark small class="mr-2">
                  <v-icon left small>mdi-alarm-light</v-icon>
                  {{ $t('autofon.alarm') }}
                </v-chip>
              </v-col>
            </v-row>
            
            <!-- Debug: Raw JSON -->
            <v-row class="mt-3">
              <v-col cols="12">
                <v-expansion-panels flat>
                  <v-expansion-panel>
                    <v-expansion-panel-header class="grey--text">
                      <v-icon small class="mr-2">mdi-code-json</v-icon>
                      Raw JSON
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <pre 
                        :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'"
                        style="max-height: 400px; overflow: auto; padding: 10px; font-size: 11px; border-radius: 4px;"
                      >{{ JSON.stringify(deviceData, null, 2) }}</pre>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Геолокация -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">
              {{ $t('autofon.geolocation') }}
              <v-progress-circular v-if="loadingPosition" indeterminate size="16" width="2" class="ml-2"></v-progress-circular>
            </h3>
            <v-row align="center">
              <v-col cols="12" md="8">
                <v-skeleton-loader v-if="loadingPosition && !position" type="text" class="my-2"></v-skeleton-loader>
                
                <div v-else-if="position">
                  <p>
                    <strong>{{ $t('autofon.coordinates') }}:</strong> 
                    {{ position.lat?.toFixed(6) }}, {{ position.lon?.toFixed(6) }}
                    <v-chip v-if="position.lbs" x-small color="warning" class="ml-2">LBS</v-chip>
                    <span v-if="position.ts" class="ml-2 grey--text text--darken-1">
                      ({{ $t('autofon.updated') }}: {{ formatDateTime(position.ts) }})
                    </span>
                  </p>
                  <p v-if="position.speed !== undefined && position.speed !== 472">
                    <strong>{{ $t('autofon.speed') }}:</strong> {{ position.speed }} {{ $t('custom.kmh') }}
                  </p>
                  <p v-if="position.satellites !== undefined && position.satellites > 0">
                    <strong>{{ $t('autofon.satellites') }}:</strong> {{ position.satellites }}
                  </p>
                </div>
                <div v-else>
                  <p class="grey--text">{{ $t('autofon.no_position_data') }}</p>
                </div>
              </v-col>
              <v-col cols="12" md="4" class="text-right">
                <v-btn color="primary" small @click="openMap" :disabled="!position" class="mr-2">
                  <v-icon left small>mdi-map-marker</v-icon>
                  {{ $t('autofon.open_on_map') }}
                </v-btn>
                <v-btn color="secondary" small @click="refreshPosition" :loading="loadingPosition">
                  <v-icon left small>mdi-refresh</v-icon>
                  {{ $t('autofon.refresh') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Управление -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('autofon.controls') }}</h3>
            
            <v-row v-if="controlsLoaded && availableControls.length === 0">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('autofon.no_commands_available') }}
                </v-alert>
              </v-col>
            </v-row>
            
            <v-row v-else align="center">
              <v-col cols="12" md="6" lg="5">
                <v-autocomplete
                  v-model="selectedCommand"
                  :items="availableControls"
                  item-text="name"
                  item-value="id"
                  :label="$t('autofon.select_command')"
                  :placeholder="$t('autofon.select_command_placeholder')"
                  outlined
                  dense
                  clearable
                  :disabled="executingCommand"
                  :loading="loadingControls"
                  return-object
                  @click="onControlsFocus"
                >
                  <template v-slot:item="{ item }">
                    <span>{{ item.name }}</span>
                  </template>
                </v-autocomplete>
                
                <!-- Параметры команды -->
                <div v-if="selectedCommand && selectedCommand.params && selectedCommand.params.length > 0" class="mb-2">
                  <div v-for="(param, idx) in selectedCommand.params" :key="idx" class="mb-2">
                    <!-- Range параметр (число) -->
                    <v-text-field
                      v-if="param.type === 'range'"
                      v-model.number="commandParams[param.param_id]"
                      :label="param.hint || $t('autofon.param_value')"
                      :hint="`${$t('autofon.param_range')}: ${param.min} - ${param.max}`"
                      persistent-hint
                      type="number"
                      :min="param.min"
                      :max="param.max"
                      outlined
                      dense
                    ></v-text-field>
                    
                    <!-- Text параметр -->
                    <v-text-field
                      v-else-if="param.type === 'text'"
                      v-model="commandParams[param.param_id]"
                      :label="param.hint || $t('autofon.param_text')"
                      :maxlength="param.max_length"
                      :counter="param.max_length"
                      outlined
                      dense
                    ></v-text-field>
                    
                    <!-- Phone параметр -->
                    <v-text-field
                      v-else-if="param.type === 'phone'"
                      v-model="commandParams[param.param_id]"
                      :label="param.hint || $t('autofon.param_phone')"
                      placeholder="+79001234567"
                      outlined
                      dense
                    ></v-text-field>
                    
                    <!-- Timezone параметр -->
                    <v-text-field
                      v-else-if="param.type === 'timezone'"
                      v-model.number="commandParams[param.param_id]"
                      :label="param.hint || $t('autofon.param_timezone')"
                      type="number"
                      :min="param.min"
                      :max="param.max"
                      outlined
                      dense
                    ></v-text-field>
                  </div>
                </div>
              </v-col>
              <v-col cols="12" md="3" lg="2">
                <v-btn
                  color="primary"
                  :loading="executingCommand"
                  :disabled="!selectedCommand || executingCommand"
                  @click="sendSelectedCommand"
                  block
                  class="mb-6"
                >
                  <v-icon left>mdi-send</v-icon>
                  {{ $t('autofon.send_command') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- Статистика за сутки -->
        <v-row v-if="deviceData && deviceData.mileage !== undefined">
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('autofon.daily_stats') }}</h3>
            <v-chip small class="mr-2 mb-1">
              {{ $t('autofon.daily_mileage') }}: {{ deviceData.mileage?.toFixed(1) || 0 }} {{ $t('custom.km') }}
            </v-chip>
            <v-chip small class="mr-2 mb-1">
              {{ $t('autofon.avg_speed') }}: {{ deviceData.avg_speed || 0 }} {{ $t('custom.kmh') }}
            </v-chip>
            <v-chip small class="mr-2 mb-1">
              {{ $t('autofon.max_speed') }}: {{ deviceData.max_speed || 0 }} {{ $t('custom.kmh') }}
            </v-chip>
            <v-chip small class="mr-2 mb-1">
              {{ $t('autofon.move_time') }}: {{ formatDurationMinutes(deviceData.move_time || 0) }}
            </v-chip>
            <v-chip v-if="deviceData.fuel_calc > 0" small class="mr-2 mb-1">
              {{ $t('autofon.fuel_calc') }}: {{ deviceData.fuel_calc }} {{ $t('custom.liters') }}
            </v-chip>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История треков -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('autofon.track_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(trackStartDate, trackStartTime)"
                      :label="$t('autofon.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="trackStartTab" fixed-tabs>
                      <v-tab>{{ $t('autofon.date') }}</v-tab>
                      <v-tab>{{ $t('autofon.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="trackStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="trackStartDate"
                          :max="today"
                          @input="trackStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="trackStartTime"
                          format="24hr"
                          @click:minute="trackStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-menu
                  v-model="trackEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(trackEndDate, trackEndTime)"
                      :label="$t('autofon.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="trackEndTab" fixed-tabs>
                      <v-tab>{{ $t('autofon.date') }}</v-tab>
                      <v-tab>{{ $t('autofon.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="trackEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="trackEndDate"
                          :max="today"
                          :min="trackStartDate"
                          @input="trackEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="trackEndTime"
                          format="24hr"
                          @click:minute="trackEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="4" md="3">
                <v-btn color="primary" @click="fetchTracks" :loading="loadingTracks" block>
                  <v-icon left>mdi-map-marker-path</v-icon>
                  {{ $t('autofon.show_tracks') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <v-row v-if="trackData">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  <strong>{{ $t('autofon.track_mileage') }}:</strong> {{ trackData.mileage }} {{ $t('custom.km') }}
                  <span class="ml-3">
                    <strong>{{ $t('autofon.track_time') }}:</strong> {{ formatDuration(trackData.moving_time) }}
                  </span>
                  <span class="ml-3">
                    <strong>{{ $t('autofon.track_points') }}:</strong> {{ trackData.way?.length || 0 }}
                  </span>
                </v-alert>
                <v-btn 
                  v-if="trackData.way?.length > 0"
                  color="primary" 
                  small 
                  @click="openTrackOnMap"
                  class="mt-2"
                >
                  <v-icon left small>mdi-map</v-icon>
                  {{ $t('autofon.open_track_on_map') }}
                </v-btn>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-divider class="my-4"></v-divider>

        <!-- История событий -->
        <v-row>
          <v-col cols="12">
            <h3 class="mb-3">{{ $t('autofon.event_history') }}</h3>
            <v-row align="center">
              <v-col cols="12" sm="6" md="3">
                <v-menu
                  v-model="eventStartMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(eventStartDate, eventStartTime)"
                      :label="$t('autofon.start_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="eventStartTab" fixed-tabs>
                      <v-tab>{{ $t('autofon.date') }}</v-tab>
                      <v-tab>{{ $t('autofon.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eventStartTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="eventStartDate"
                          :max="today"
                          @input="eventStartTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="eventStartTime"
                          format="24hr"
                          @click:minute="eventStartMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-menu
                  v-model="eventEndMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="formatDateTimeInput(eventEndDate, eventEndTime)"
                      :label="$t('autofon.end_datetime')"
                      prepend-icon="mdi-calendar-clock"
                      readonly
                      v-bind="attrs"
                      v-on="on"
                      hide-details
                      outlined
                      dense
                    ></v-text-field>
                  </template>
                  <v-card>
                    <v-tabs v-model="eventEndTab" fixed-tabs>
                      <v-tab>{{ $t('autofon.date') }}</v-tab>
                      <v-tab>{{ $t('autofon.time') }}</v-tab>
                    </v-tabs>
                    <v-tabs-items v-model="eventEndTab">
                      <v-tab-item>
                        <v-date-picker
                          v-model="eventEndDate"
                          :max="today"
                          :min="eventStartDate"
                          @input="eventEndTab = 1"
                        ></v-date-picker>
                      </v-tab-item>
                      <v-tab-item>
                        <v-time-picker
                          v-model="eventEndTime"
                          format="24hr"
                          @click:minute="eventEndMenu = false"
                        ></v-time-picker>
                      </v-tab-item>
                    </v-tabs-items>
                  </v-card>
                </v-menu>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-select
                  v-model="eventMinLevel"
                  :items="eventLevels"
                  :label="$t('autofon.event_level')"
                  item-text="text"
                  item-value="value"
                  outlined
                  dense
                  hide-details
                ></v-select>
              </v-col>
              <v-col cols="12" sm="6" md="3">
                <v-btn color="primary" @click="fetchMessages(true)" :loading="loadingMessages" block>
                  <v-icon left>mdi-history</v-icon>
                  {{ $t('autofon.show_events') }}
                </v-btn>
              </v-col>
            </v-row>
            
            <!-- Счетчик сообщений -->
            <v-row v-if="messagesTotal > 0" class="mt-2">
              <v-col cols="12">
                <v-chip small outlined color="primary">
                  {{ $t('autofon.events_loaded') }}: {{ messages.length }} / {{ messagesTotal }}
                </v-chip>
              </v-col>
            </v-row>
            
            <v-row v-if="messages && messages.length > 0">
              <v-col cols="12">
                <v-simple-table dense class="events-table">
                  <template v-slot:default>
                    <thead>
                      <tr>
                        <th style="width: 50px"></th>
                        <th style="width: 180px">{{ $t('autofon.event_time') }}</th>
                        <th>{{ $t('autofon.event_text') }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(message, index) in messages" :key="index">
                        <td class="text-center">
                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon 
                                :color="getMessageLevelColor(message.text)" 
                                small
                                v-bind="attrs"
                                v-on="on"
                              >
                                {{ getMessageLevelIcon(message.text) }}
                              </v-icon>
                            </template>
                            <span>{{ getMessageLevelText(message.text) }}</span>
                          </v-tooltip>
                        </td>
                        <td class="text-no-wrap">{{ formatDateTime(message.ts) }}</td>
                        <td>{{ message.text }}</td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
                
                <!-- Кнопка загрузить ещё -->
                <div v-if="hasMoreMessages" class="text-center mt-4">
                  <v-btn 
                    color="primary" 
                    outlined 
                    @click="loadMoreMessages" 
                    :loading="loadingMoreMessages"
                  >
                    <v-icon left>mdi-reload</v-icon>
                    {{ $t('autofon.load_more') }}
                  </v-btn>
                </div>
              </v-col>
            </v-row>
            <v-row v-else-if="messagesLoaded">
              <v-col cols="12">
                <v-alert type="info" dense outlined>
                  {{ $t('autofon.no_events') }}
                </v-alert>
              </v-col>
            </v-row>
          </v-col>
        </v-row>

      </v-container>
    </v-card-text>

    <!-- Snackbar для уведомлений -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          {{ $t('custom.close') }}
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Диалог с картой трека -->
    <v-dialog v-model="trackMapDialog" max-width="900" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between">
          <span>{{ $t('autofon.track_on_map') }}</span>
          <v-btn icon @click="trackMapDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <GmapMap
            v-if="trackMapDialog && trackPath.length > 0"
            :center="trackCenter"
            :zoom="trackZoom"
            style="width: 100%; height: 500px;"
            :options="{
              zoomControl: true,
              mapTypeControl: true,
              scaleControl: true,
              streetViewControl: false,
              rotateControl: false,
              fullscreenControl: true
            }"
          >
            <!-- Линия трека -->
            <GmapPolyline
              :path="trackPath"
              :options="{
                strokeColor: '#1976D2',
                strokeOpacity: 0.9,
                strokeWeight: 4
              }"
            />
            <!-- Маркер начала -->
            <GmapMarker
              :position="trackPath[0]"
              :icon="{
                url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
              }"
              :title="$t('autofon.track_start')"
            />
            <!-- Маркер конца -->
            <GmapMarker
              :position="trackPath[trackPath.length - 1]"
              :icon="{
                url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
              }"
              :title="$t('autofon.track_end')"
            />
            <!-- Маркеры событий -->
            <GmapMarker
              v-for="(event, index) in trackEvents"
              :key="'event-' + index"
              :position="event.position"
              :icon="getEventIcon(event.type)"
              :title="event.title"
              @click="selectedEvent = event"
            />
            <!-- Инфо-окно для выбранного события -->
            <GmapInfoWindow
              v-if="selectedEvent"
              :position="selectedEvent.position"
              :opened="!!selectedEvent"
              @closeclick="selectedEvent = null"
            >
              <div class="pa-2">
                <strong>{{ selectedEvent.title }}</strong>
                <div class="mt-1 text-caption">{{ selectedEvent.time }}</div>
                <div v-if="selectedEvent.text" class="mt-1">{{ selectedEvent.text }}</div>
              </div>
            </GmapInfoWindow>
          </GmapMap>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="trackMapDialog = false">
            {{ $t('custom.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
export default {
  name: "AutofonTab",
  props: {
    car: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      // Загрузка
      loadingDetails: false,
      loadingPosition: false,
      loadingControls: false,
      loadingTracks: false,
      loadingMessages: false,
      executingCommand: false,
      
      // Данные устройства
      deviceData: null,
      position: null,
      
      // Управление
      availableControls: [],
      controlsLoaded: false,
      selectedCommand: null,
      commandParams: {}, // Параметры для выбранной команды
      
      // Карта трека
      trackMapDialog: false,
      trackPath: [],
      trackCenter: { lat: 55.75, lng: 37.62 },
      trackZoom: 12,
      trackEvents: [],
      selectedEvent: null,
      
      // Треки
      trackStartDate: null,
      trackStartTime: '00:00',
      trackEndDate: null,
      trackEndTime: '23:59',
      trackStartMenu: false,
      trackEndMenu: false,
      trackStartTab: 0,
      trackEndTab: 0,
      trackData: null,
      
      // Сообщения
      eventStartDate: null,
      eventStartTime: '00:00',
      eventEndDate: null,
      eventEndTime: '23:59',
      eventStartMenu: false,
      eventEndMenu: false,
      eventStartTab: 0,
      eventEndTab: 0,
      eventMinLevel: null, // null = все сообщения
      messages: [],
      messagesLoaded: false,
      messagesTotal: 0,
      messagesOffset: 0,
      messagesLimit: 10, // Загружаем по 10 сообщений
      loadingMoreMessages: false,
      
      // Snackbar
      snackbar: false,
      snackbarText: "",
      snackbarColor: "success"
    };
  },
  computed: {
    today() {
      return new Date().toISOString().substr(0, 10);
    },
    statusColor() {
      const status = this.deviceData?.status;
      if (status === 'online' || status === 'moving') return 'success';
      if (status === 'idle') return 'warning';
      return 'grey';
    },
    // Уровни событий для AutoFon
    eventLevels() {
      return [
        { value: null, text: this.$t('autofon.level_all') },
        { value: 1, text: this.$t('autofon.level_critical') },
        { value: 2, text: this.$t('autofon.level_alarm') },
        { value: 3, text: this.$t('autofon.level_warning') },
        { value: 4, text: this.$t('autofon.level_data') },
        { value: 5, text: this.$t('autofon.level_info') },
        { value: 6, text: this.$t('autofon.level_ignore') }
      ];
    },
    hasMoreMessages() {
      return this.messages.length < this.messagesTotal;
    },
    deviceInfoItems() {
      if (!this.deviceData) return [];
      
      const items = [];
      
      // ID устройства
      if (this.deviceData.id) {
        items.push({
          icon: 'mdi-identifier',
          label: this.$t('autofon.device_id'),
          value: this.deviceData.id
        });
      }
      
      // Спутники GPS
      if (this.deviceData.position?.satellites !== undefined) {
        items.push({
          icon: 'mdi-satellite-variant',
          label: this.$t('autofon.satellites'),
          value: this.deviceData.position.satellites,
          color: this.deviceData.position.satellites >= 4 ? 'success' : 'warning'
        });
      }
      
      // GSM сигнал
      if (this.deviceData.gsm_signal !== undefined && this.deviceData.gsm_signal > 0 && this.deviceData.gsm_signal !== 99) {
        items.push({
          icon: 'mdi-signal',
          label: this.$t('autofon.gsm_signal'),
          value: `${this.deviceData.gsm_signal}%`
        });
      }
      
      // Текущая скорость
      if (this.deviceData.position?.speed !== undefined) {
        items.push({
          icon: 'mdi-speedometer',
          label: this.$t('autofon.current_speed'),
          value: `${this.deviceData.position.speed} ${this.$t('custom.kmh')}`
        });
      }
      
      // Средняя скорость
      if (this.deviceData.avg_speed !== undefined && this.deviceData.avg_speed > 0) {
        items.push({
          icon: 'mdi-speedometer-medium',
          label: this.$t('autofon.avg_speed'),
          value: `${this.deviceData.avg_speed} ${this.$t('custom.kmh')}`
        });
      }
      
      // Максимальная скорость
      if (this.deviceData.max_speed !== undefined && this.deviceData.max_speed > 0) {
        items.push({
          icon: 'mdi-speedometer-slow',
          label: this.$t('autofon.max_speed'),
          value: `${this.deviceData.max_speed} ${this.$t('custom.kmh')}`
        });
      }
      
      // Пробег за день
      if (this.deviceData.mileage !== undefined) {
        items.push({
          icon: 'mdi-counter',
          label: this.$t('autofon.daily_mileage'),
          value: `${this.deviceData.mileage?.toFixed(1) || 0} ${this.$t('custom.km')}`
        });
      }
      
      // Время в движении (приходит в минутах от API)
      if (this.deviceData.move_time !== undefined && this.deviceData.move_time > 0) {
        const hours = Math.floor(this.deviceData.move_time / 60);
        const minutes = this.deviceData.move_time % 60;
        items.push({
          icon: 'mdi-timer-outline',
          label: this.$t('autofon.move_time'),
          value: hours > 0 ? `${hours}${this.$t('custom.h')} ${minutes}${this.$t('custom.min')}` : `${minutes}${this.$t('custom.min')}`
        });
      }
      
      // Расход топлива за день
      if (this.deviceData.fuel_calc !== undefined && this.deviceData.fuel_calc > 0) {
        items.push({
          icon: 'mdi-gas-station',
          label: this.$t('autofon.fuel_calc'),
          value: `${this.deviceData.fuel_calc} ${this.$t('custom.liters')}`
        });
      }
      
      // Статус GPS
      if (this.deviceData.gps) {
        const gpsStatus = this.getGpsStatusText(this.deviceData.gps);
        items.push({
          icon: 'mdi-satellite-uplink',
          label: this.$t('autofon.gps_status'),
          value: gpsStatus.text,
          color: gpsStatus.color
        });
      }
      
      // Статус зажигания
      if (this.deviceData.ignition) {
        const ignStatus = this.getIgnitionStatusText(this.deviceData.ignition);
        items.push({
          icon: 'mdi-key',
          label: this.$t('autofon.ignition'),
          value: ignStatus.text,
          color: ignStatus.color
        });
      }
      
      // Статус питания
      if (this.deviceData.power) {
        const powerStatus = this.getPowerStatusText(this.deviceData.power);
        items.push({
          icon: 'mdi-power-plug',
          label: this.$t('autofon.power_status'),
          value: powerStatus.text,
          color: powerStatus.color
        });
      }
      
      // Признак движения
      if (this.deviceData.moving !== undefined) {
        items.push({
          icon: this.deviceData.moving ? 'mdi-car-side' : 'mdi-car-brake-parking',
          label: this.$t('autofon.movement'),
          value: this.deviceData.moving ? this.$t('autofon.moving') : this.$t('autofon.parked'),
          color: this.deviceData.moving ? 'info' : ''
        });
      }
      
      // Внешнее напряжение (бортовое)
      if (this.deviceData.external_voltage !== undefined && this.deviceData.external_voltage > 0 && this.deviceData.external_voltage !== 65535) {
        const voltage = (this.deviceData.external_voltage / 1000).toFixed(1);
        items.push({
          icon: 'mdi-car-battery',
          label: this.$t('autofon.external_voltage'),
          value: `${voltage} V`,
          color: voltage < 11 ? 'error' : (voltage < 12 ? 'warning' : 'success')
        });
      }
      
      // Напряжение батареи маяка
      if (this.deviceData.battery_voltage !== undefined && this.deviceData.battery_voltage > 0) {
        const voltage = (this.deviceData.battery_voltage / 1000).toFixed(2);
        items.push({
          icon: 'mdi-battery',
          label: this.$t('autofon.battery_voltage'),
          value: `${voltage} V`,
          color: voltage < 3.5 ? 'error' : (voltage < 3.7 ? 'warning' : 'success')
        });
      }
      
      // Температура
      if (this.deviceData.temperature !== undefined) {
        items.push({
          icon: 'mdi-thermometer',
          label: this.$t('autofon.temperature'),
          value: `${this.deviceData.temperature}°C`,
          color: this.deviceData.temperature < 0 ? 'blue' : (this.deviceData.temperature > 40 ? 'error' : '')
        });
      }
      
      // Курс (направление)
      if (this.deviceData.position?.course !== undefined) {
        items.push({
          icon: 'mdi-compass',
          label: this.$t('autofon.course'),
          value: `${this.deviceData.position.course}°`
        });
      }
      
      // Последнее обновление
      if (this.deviceData.last_update_ts) {
        const date = new Date(this.deviceData.last_update_ts * 1000);
        items.push({
          icon: 'mdi-clock-outline',
          label: this.$t('autofon.last_update'),
          value: date.toLocaleString()
        });
      }
      
      return items;
    }
  },
  watch: {
    // Сброс и инициализация параметров при смене команды
    selectedCommand(newCmd) {
      this.commandParams = {};
      if (newCmd && newCmd.params && newCmd.params.length > 0) {
        newCmd.params.forEach(param => {
          // Устанавливаем значение по умолчанию если есть
          if (param.default !== undefined && param.default !== null) {
            this.commandParams[param.param_id] = param.default;
          }
        });
      }
    }
  },
  mounted() {
    this.initializeDates();
    this.fetchDetails();
    this.fetchPosition();
  },
  methods: {
    getAuthHeaders() {
      return {
        headers: {
          Authorization: this.$store.getters.getAuthToken,
        },
      };
    },
    
    initializeDates() {
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      this.trackStartDate = yesterday.toISOString().substr(0, 10);
      this.trackEndDate = today.toISOString().substr(0, 10);
      this.eventStartDate = yesterday.toISOString().substr(0, 10);
      this.eventEndDate = today.toISOString().substr(0, 10);
    },
    
    async fetchDetails() {
      this.loadingDetails = true;
      try {
        const response = await this.axios.get(`/api/v1/autofon/${this.car.id}/details`, this.getAuthHeaders());
        this.deviceData = response.data.data || response.data;
        
        // Обновляем позицию из данных устройства
        if (this.deviceData.position) {
          this.position = this.deviceData.position;
        }
      } catch (error) {
        console.error('Failed to fetch Autofon details:', error);
        this.showSnackbar(this.$t('autofon.error_fetch_details'), 'error');
      } finally {
        this.loadingDetails = false;
      }
    },
    
    async fetchPosition() {
      this.loadingPosition = true;
      try {
        const response = await this.axios.get(`/api/v1/autofon/${this.car.id}/position`, this.getAuthHeaders());
        this.position = response.data;
      } catch (error) {
        console.error('Failed to fetch position:', error);
      } finally {
        this.loadingPosition = false;
      }
    },
    
    refreshPosition() {
      this.fetchPosition();
    },
    
    async onControlsFocus() {
      if (this.controlsLoaded) return;
      
      this.loadingControls = true;
      try {
        const response = await this.axios.get(`/api/v1/autofon/${this.car.id}/controls_library`, this.getAuthHeaders());
        this.availableControls = response.data.controls || [];
        this.controlsLoaded = true;
      } catch (error) {
        console.error('Failed to fetch controls:', error);
        this.showSnackbar(this.$t('autofon.error_fetch_controls'), 'error');
      } finally {
        this.loadingControls = false;
      }
    },
    
    async sendSelectedCommand() {
      if (!this.selectedCommand) return;
      
      this.executingCommand = true;
      try {
        // Формируем payload с параметрами
        const payload = {
          command_id: this.selectedCommand.id,
          params: this.commandParams
        };
        
        const response = await this.axios.post(`/api/v1/autofon/${this.car.id}/command`, payload, this.getAuthHeaders());
        
        if (response.data.success) {
          this.showSnackbar(this.$t('autofon.command_sent'), 'success');
          // Обновляем данные устройства через 2 секунды
          setTimeout(() => this.fetchDetails(), 2000);
        } else {
          this.showSnackbar(response.data.error || this.$t('autofon.command_failed'), 'error');
        }
      } catch (error) {
        console.error('Failed to send command:', error);
        this.showSnackbar(error.response?.data?.error || this.$t('autofon.command_failed'), 'error');
      } finally {
        this.executingCommand = false;
        this.selectedCommand = null;
        this.commandParams = {};
      }
    },
    
    async fetchTracks() {
      this.loadingTracks = true;
      try {
        // Используем выбранные дату и время
        const beginTs = new Date(`${this.trackStartDate}T${this.trackStartTime}:00`).getTime() / 1000;
        const endTs = new Date(`${this.trackEndDate}T${this.trackEndTime}:59`).getTime() / 1000;
        
        const response = await this.axios.get(`/api/v1/autofon/${this.car.id}/tracks`, {
          params: { begin: Math.floor(beginTs), end: Math.floor(endTs) },
          ...this.getAuthHeaders()
        });
        
        if (response.data.success) {
          this.trackData = response.data;
        } else {
          this.showSnackbar(response.data.error || this.$t('autofon.error_fetch_tracks'), 'error');
        }
      } catch (error) {
        console.error('Failed to fetch tracks:', error);
        this.showSnackbar(this.$t('autofon.error_fetch_tracks'), 'error');
      } finally {
        this.loadingTracks = false;
      }
    },
    
    async fetchMessages(reset = true) {
      this.loadingMessages = true;
      try {
        // Используем выбранные дату и время
        const periodStart = new Date(`${this.eventStartDate}T${this.eventStartTime}:00`).getTime() / 1000;
        const periodEnd = new Date(`${this.eventEndDate}T${this.eventEndTime}:59`).getTime() / 1000;
        
        // Сбрасываем offset при новом запросе
        if (reset) {
          this.messagesOffset = 0;
          this.messages = [];
        }
        
        const params = { 
          begin: Math.floor(periodStart), 
          end: Math.floor(periodEnd),
          limit: this.messagesLimit,
          offset: this.messagesOffset
        };
        
        // Добавляем minlevel если выбран
        if (this.eventMinLevel !== null) {
          params.minlevel = this.eventMinLevel;
        }
        
        const response = await this.axios.get(`/api/v1/autofon/${this.car.id}/messages`, {
          params,
          ...this.getAuthHeaders()
        });
        
        const newMessages = response.data.messages || [];
        this.messagesTotal = response.data.total || 0;
        
        if (reset) {
          this.messages = newMessages;
        } else {
          this.messages = [...this.messages, ...newMessages];
        }
        
        this.messagesLoaded = true;
      } catch (error) {
        console.error('Failed to fetch messages:', error);
        this.showSnackbar(this.$t('autofon.error_fetch_events'), 'error');
      } finally {
        this.loadingMessages = false;
      }
    },
    
    async loadMoreMessages() {
      this.loadingMoreMessages = true;
      try {
        this.messagesOffset += this.messagesLimit;
        await this.fetchMessages(false);
      } finally {
        this.loadingMoreMessages = false;
      }
    },
    
    openMap() {
      if (!this.position) return;
      
      const { lat, lon } = this.position;
      const locale = this.$i18n.locale;
      
      // Используем Яндекс.Карты для ru, Google Maps для остальных
      const url = locale === 'ru'
        ? `https://yandex.ru/maps/?pt=${lon},${lat}&z=15&l=map`
        : `https://www.google.com/maps?q=${lat},${lon}`;
      
      window.open(url, '_blank');
    },
    
    async openTrackOnMap() {
      if (!this.trackData?.way?.length) return;
      
      const way = this.trackData.way;
      
      // Преобразуем точки в формат для Google Maps
      this.trackPath = way.map(p => ({ lat: p.lat, lng: p.lon }));
      
      // Отладка: проверяем сколько точек и их формат
      console.log('[AutofonTab] trackPath points:', this.trackPath.length);
      console.log('[AutofonTab] first point:', this.trackPath[0]);
      console.log('[AutofonTab] last point:', this.trackPath[this.trackPath.length - 1]);
      // Проверим валидность точек
      const invalidPoints = this.trackPath.filter(p => !p.lat || !p.lng || isNaN(p.lat) || isNaN(p.lng));
      if (invalidPoints.length > 0) {
        console.warn('[AutofonTab] Invalid points found:', invalidPoints.length);
      }
      
      // Вычисляем центр трека
      const lats = way.map(p => p.lat);
      const lons = way.map(p => p.lon);
      const centerLat = (Math.min(...lats) + Math.max(...lats)) / 2;
      const centerLon = (Math.min(...lons) + Math.max(...lons)) / 2;
      this.trackCenter = { lat: centerLat, lng: centerLon };
      
      // Вычисляем масштаб на основе размера трека
      const latDiff = Math.max(...lats) - Math.min(...lats);
      const lonDiff = Math.max(...lons) - Math.min(...lons);
      const maxDiff = Math.max(latDiff, lonDiff);
      
      if (maxDiff > 1) this.trackZoom = 8;
      else if (maxDiff > 0.5) this.trackZoom = 9;
      else if (maxDiff > 0.2) this.trackZoom = 10;
      else if (maxDiff > 0.1) this.trackZoom = 11;
      else if (maxDiff > 0.05) this.trackZoom = 12;
      else if (maxDiff > 0.02) this.trackZoom = 13;
      else this.trackZoom = 14;
      
      // Загружаем события для того же периода
      this.trackEvents = [];
      this.selectedEvent = null;
      try {
        const beginTs = new Date(this.trackStartDate).getTime() / 1000;
        const endTs = new Date(this.trackEndDate + 'T23:59:59').getTime() / 1000;
        
        const response = await this.axios.get(`/api/v1/autofon/${this.car.id}/messages`, {
          params: { begin: Math.floor(beginTs), end: Math.floor(endTs) },
          ...this.getAuthHeaders()
        });
        
        if (response.data && Array.isArray(response.data) && way.length > 0) {
          // События не содержат координаты, находим ближайшую точку трека по времени
          this.trackEvents = response.data
            .filter(e => e.ts)
            .map(e => {
              // Находим ближайшую точку трека по времени
              const eventTs = e.ts;
              let closestPoint = way[0];
              let minDiff = Math.abs((way[0].ts || 0) - eventTs);
              
              for (const point of way) {
                const diff = Math.abs((point.ts || 0) - eventTs);
                if (diff < minDiff) {
                  minDiff = diff;
                  closestPoint = point;
                }
              }
              
              return {
                position: { lat: closestPoint.lat, lng: closestPoint.lon },
                type: e.type?.toString() || 'info',
                title: this.getEventTitle(e),
                time: this.formatDateTime(e.ts),
                text: e.text || ''
              };
            });
        }
      } catch (error) {
        console.error('Failed to load events for track:', error);
      }
      
      // Открываем диалог с картой
      this.trackMapDialog = true;
    },
    
    getEventTitle(event) {
      const type = event.type || '';
      if (type.includes('stop') || type.includes('parking')) return this.$t('autofon.event_stop');
      if (type.includes('ignition_on')) return this.$t('autofon.event_ignition_on');
      if (type.includes('ignition_off')) return this.$t('autofon.event_ignition_off');
      if (type.includes('alarm')) return this.$t('autofon.event_alarm');
      if (type.includes('speed')) return this.$t('autofon.event_speed');
      return event.text || this.$t('autofon.event_info');
    },
    
    getEventIcon(type) {
      // Иконки для разных типов событий
      const icons = {
        stop: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        parking: 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png',
        ignition_on: 'https://maps.google.com/mapfiles/ms/icons/yellow-dot.png',
        ignition_off: 'https://maps.google.com/mapfiles/ms/icons/orange-dot.png',
        alarm: 'https://maps.google.com/mapfiles/ms/icons/purple-dot.png',
        speed: 'https://maps.google.com/mapfiles/ms/icons/pink-dot.png',
        info: 'https://maps.google.com/mapfiles/ms/icons/ltblue-dot.png'
      };
      
      if (type.includes('stop') || type.includes('parking')) return { url: icons.parking };
      if (type.includes('ignition_on')) return { url: icons.ignition_on };
      if (type.includes('ignition_off')) return { url: icons.ignition_off };
      if (type.includes('alarm')) return { url: icons.alarm };
      if (type.includes('speed')) return { url: icons.speed };
      return { url: icons.info };
    },
    
    // Цвет уровня сообщения AutoFon по тексту
    getMessageLevelColor(text) {
      const textLower = String(text || '').toLowerCase();
      
      // Тревога/Авария - красный
      if (textLower.includes('тревога') || textLower.includes('авария') || textLower.includes('alarm') || 
          textLower.includes('потеряна связь') || textLower.includes('отключен')) {
        return 'error';
      }
      // Внеочередной пакет - оранжевый (предупреждение)
      if (textLower.includes('внеочередной')) {
        return 'warning';
      }
      // Восстановление - зеленый
      if (textLower.includes('восстановлен') || textLower.includes('включен')) {
        return 'success';
      }
      // Информация - синий (по умолчанию)
      return 'info';
    },
    
    // Иконка уровня сообщения по тексту
    getMessageLevelIcon(text) {
      const textLower = String(text || '').toLowerCase();
      
      if (textLower.includes('тревога') || textLower.includes('авария') || textLower.includes('alarm') ||
          textLower.includes('потеряна связь') || textLower.includes('отключен')) {
        return 'mdi-alert-circle';
      }
      if (textLower.includes('внеочередной')) {
        return 'mdi-alert';
      }
      if (textLower.includes('восстановлен') || textLower.includes('включен')) {
        return 'mdi-check-circle';
      }
      return 'mdi-information';
    },
    
    // Текст уровня сообщения
    getMessageLevelText(text) {
      const textLower = String(text || '').toLowerCase();
      
      if (textLower.includes('тревога') || textLower.includes('авария') || textLower.includes('alarm') ||
          textLower.includes('потеряна связь') || textLower.includes('отключен')) {
        return this.$t('autofon.level_alarm');
      }
      if (textLower.includes('внеочередной')) {
        return this.$t('autofon.level_warning');
      }
      if (textLower.includes('восстановлен') || textLower.includes('включен')) {
        return this.$t('autofon.level_info');
      }
      return this.$t('autofon.level_data');
    },
    
    formatDateTime(timestamp) {
      if (!timestamp) return '-';
      const date = new Date(timestamp * 1000);
      return date.toLocaleString(this.$i18n.locale);
    },
    
    // Форматирование даты и времени для input поля
    formatDateTimeInput(date, time) {
      if (!date) return '';
      // Форматируем дату как DD.MM.YYYY HH:MM
      const [year, month, day] = date.split('-');
      return `${day}.${month}.${year} ${time || '00:00'}`;
    },
    
    formatDuration(seconds) {
      if (!seconds) return '0';
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}${this.$t('custom.h')} ${minutes}${this.$t('custom.m')}`;
      }
      return `${minutes}${this.$t('custom.m')}`;
    },
    
    // Форматирование времени из минут (для суточной статистики)
    formatDurationMinutes(minutes) {
      if (!minutes) return '0';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      
      if (hours > 0) {
        return `${hours}${this.$t('custom.h')} ${mins}${this.$t('custom.m')}`;
      }
      return `${mins}${this.$t('custom.m')}`;
    },
    
    getGpsStatusText(status) {
      const statuses = {
        'unknown': { text: this.$t('autofon.status_unknown'), color: '' },
        'normal': { text: this.$t('autofon.gps_normal'), color: 'success' },
        'crash': { text: this.$t('autofon.gps_crash'), color: 'error' },
        'no_valid_data': { text: this.$t('autofon.gps_no_data'), color: 'warning' }
      };
      return statuses[status] || statuses['unknown'];
    },
    
    getIgnitionStatusText(status) {
      const statuses = {
        'unknown': { text: this.$t('autofon.status_unknown'), color: '' },
        'off': { text: this.$t('autofon.ignition_off'), color: '' },
        'on': { text: this.$t('autofon.ignition_on'), color: 'success' }
      };
      return statuses[status] || statuses['unknown'];
    },
    
    getPowerStatusText(status) {
      const statuses = {
        'unknown': { text: this.$t('autofon.status_unknown'), color: '' },
        'normal': { text: this.$t('autofon.power_normal'), color: 'success' },
        'normal_notified': { text: this.$t('autofon.power_normal'), color: 'success' },
        'fail': { text: this.$t('autofon.power_fail'), color: 'error' },
        'fail_notified': { text: this.$t('autofon.power_fail'), color: 'error' }
      };
      return statuses[status] || statuses['unknown'];
    },
    
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
.v-list-item {
  min-height: auto;
}
</style>
